/**
 * Barra de controles da sala: microfone, câmera, compartilhar a tela (onde o navegador captura a tela), marcador de
 * momento (chamadas com transcrição), gravar a chamada (só de quem a criou),
 * compartilhar link (salas por link), mais opções e encerrar (ou sair). Na sala ampliada, o botão do painel da
 * chamada fica no canto direito; no celular ele entra junto dos outros (a barra usa o tamanho compacto e quebra
 * de linha em vez de estourar a largura). Fica embaixo do vídeo; no celular do convidado ela flutua sobre o vídeo,
 * com degradê.
 */
import { cn } from '@/lib/utils';
import { EndButton } from './EndButton';
import { FlipCameraButton } from './FlipCameraButton';
import { RecordButton } from '../recording/RecordButton';
import { MediaControl } from './MediaControl';
import { MoreMenu } from './MoreMenu';
import { ScreenShareButton } from './ScreenShareButton';
import { ShareLinkButton } from './ShareLinkButton';
import { MomentButton, PanelToggleButton } from './TranscriptControls';

const BAR = Object.freeze({
    compact: 'flex items-center justify-center bg-neutral-900 px-3 py-2.5',
    room: 'relative flex h-[88px] shrink-0 items-center justify-center bg-neutral-950 px-5',
    guest:
        'absolute inset-x-0 bottom-0 z-10 flex items-center justify-center bg-gradient-to-t from-black/80 to-transparent px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-10 sm:static sm:h-[88px] sm:shrink-0 sm:bg-neutral-950 sm:bg-none sm:px-5 sm:pb-0 sm:pt-0',
});

const GROUP = Object.freeze({
    // `flex-wrap`: numa janela estreita (celular de 360 px) os botões descem para a linha de baixo, nunca saem da tela.
    // `gap-1.5` (e não 2): com o compartilhar tela, a janelinha da Inbox (420 px) com todos os botões da transcrição só
    // cabe numa linha assim.
    compact: 'flex flex-wrap items-center justify-center gap-1.5',
    // Os controles ficam juntos numa pílula (esboço 3.3), com o relógio à esquerda e o botão do painel à direita.
    room: 'flex items-center gap-3 rounded-full border border-white/10 bg-neutral-900 p-2',
    guest: 'flex items-center gap-2.5 sm:gap-3',
});

/**
 * @param {{
 *   variant: 'compact' | 'room' | 'guest', hangUpLabel: string, onHangUp: () => void,
 *   onLeaveRoom?: () => void, onCopyLink?: (doc: Document) => unknown, root: HTMLElement | null,
 *   shareRef?: React.Ref<HTMLButtonElement>, popout?: object, panelToggle?: boolean, panelToggleInline?: boolean,
 *   panelVariant?: 'room'|'compact',
 * }} props: `shareRef` aponta para o botão de compartilhar (destino da animação do convite). `popout`
 *   é o controle da janela flutuante (ver `MoreMenu`). `panelToggle`: mostra o botão que mostra e esconde a transcrição
 *   (padrão: só na sala ampliada; a sala o pede em todas as janelas de uma chamada com a Jev); `panelToggleInline`: ele
 *   fica junto dos outros botões, e não no canto direito (só a sala ampliada larga o põe no canto); `panelVariant`:
 *   a janela de que o painel é (ver `PanelToggleButton`). `elapsedLabel`: o cronômetro, que a sala ampliada mostra no
 *   canto esquerdo da barra (só visual: o cabeçalho da janela já tem o cronômetro com nome acessível).
 */
export function RoomControlBar({
    variant, hangUpLabel, onHangUp, onLeaveRoom, onCopyLink, root, shareRef, popout, panelToggle = variant === 'room',
    panelToggleInline = variant !== 'room', panelVariant = variant, elapsedLabel,
}) {
    return (
        <footer className={BAR[variant]}>
            {variant === 'room' && elapsedLabel && (
                <span aria-hidden="true" data-bar-clock="" className="absolute left-5 top-1/2 -translate-y-1/2 font-mono text-sm tabular-nums text-neutral-300">
                    {elapsedLabel}
                </span>
            )}
            <div className={cn(GROUP[variant])}>
                <MediaControl kind="mic" variant={variant} />
                <MediaControl kind="cam" variant={variant} />
                {variant === 'guest' && <FlipCameraButton variant={variant} />}
                {/* Só onde o navegador captura a tela (no celular não aparece). */}
                <ScreenShareButton variant={variant} />
                {/* Só existe com a Jev na chamada. A legenda não tem botão aqui: fica no menu "Mais opções". */}
                {variant !== 'guest' && <MomentButton variant={variant} />}
                {/* Gravar a chamada: só de quem a criou, e só onde o provedor da gravação está na sala (senão não aparece). */}
                {variant !== 'guest' && <RecordButton variant={variant} />}
                {panelToggle && panelToggleInline && <PanelToggleButton variant={variant} panelVariant={panelVariant} />}
                {onCopyLink && <ShareLinkButton ref={shareRef} variant={variant} onClick={onCopyLink} />}
                <MoreMenu variant={variant} root={root} onLeaveRoom={onLeaveRoom} popout={popout} />
                <EndButton label={hangUpLabel} variant={variant} onClick={onHangUp} />
            </div>
            {panelToggle && !panelToggleInline && (
                <div className="absolute right-5 top-1/2 -translate-y-1/2">
                    <PanelToggleButton variant={variant} panelVariant={panelVariant} />
                </div>
            )}
        </footer>
    );
}
